import { OpponentAvatar } from '@/components/schedule/OpponentAvatar';
import type { MatchDisplay, MatchSide } from '@/lib/matchDisplay';
import { cn } from '@/lib/utils';

// MatchSides renders a fixture's two sides home-first, each with its own crest:
// your club's logo next to your team, the opponent's next to theirs. Shared by
// every calendar surface so a game reads the same wherever it lands.
//
// Orientation is a readability call, not a style preference:
//
//   'stacked' — one side per line. The only layout that survives a narrow
//     column: "A-lag – Malbas Basketbollklubb" on one line inside the Home
//     widget or a single day column of the week grid truncates BOTH names down
//     to "A… – Malbas Basket…", which tells you nothing. Use this anywhere the
//     available width is a fraction of the viewport.
//
//   'inline' — both sides on one line with an en dash. Only for full-width rows
//     (the list view) or single-line cells that can't grow taller (month grid),
//     where a second line isn't an option anyway.
//
// Either way each side is its own min-w-0 flex box, so a long club name
// truncates on its own instead of shoving the other team out of the card.
export function MatchSides({
  match,
  size,
  className,
  orientation = 'inline',
}: {
  match: MatchDisplay;
  size: number;
  className?: string;
  orientation?: 'inline' | 'stacked';
}) {
  if (orientation === 'stacked') {
    return (
      <span className={cn('flex min-w-0 flex-col gap-0.5', className)}>
        <Side side={match.home} size={size} />
        <Side side={match.away} size={size} />
      </span>
    );
  }
  return (
    <span className={cn('flex min-w-0 items-center gap-1.5', className)}>
      <Side side={match.home} size={size} />
      <span className="shrink-0 font-normal text-foreground/50">–</span>
      <Side side={match.away} size={size} />
    </span>
  );
}

function Side({ side, size }: { side: MatchSide; size: number }) {
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <OpponentAvatar src={side.logoUrl} name={side.name} size={size} />
      <span className="truncate">{side.name}</span>
    </span>
  );
}
